// src/components/module/compras/CompraForm.tsx
// Rediseño corporativo estilo Notion/Linear
import React, { useContext, useEffect, useState } from "react";
import {
  TextField,
  Button,
  Grid,
  Autocomplete,
  Box,
  Paper,
  Typography,
  Alert,
  Stack,
  Fade,
  CircularProgress,
  alpha,
  InputAdornment,
  Collapse,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  ShoppingCart as ShoppingCartIcon,
  ArrowBack,
  Save,
  Business,
  CalendarToday,
  LocalShipping,
  Phone,
  Email,
  LocationOn,
  Info,
  CheckCircle,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import {
  useProveedoresApi,
  ProveedorItem,
} from "../../../services/api/proveedoresApi";
import comprasApi from "../../../services/api/comprasApi";
import ActionPageLayout from "../../layout/layout/ActionPageLayout/ActionPageLayout";
import { DarkModeContext } from "../../../context/darkModeContext";

interface CompraFormInputs {
  IdProveedor: number;
  OrdenCompra: string;
  FechaSolicitud: Date;
  FechaEntrega: Date | null;
  IdEstadoCompra: number;
  IdUsuario: number;
  Activo: boolean;
}

// ============================================================
// PALETA NOTION/LINEAR
// ============================================================
const PALETTE = {
  light: {
    bg: "#FFFFFF",
    bgSecondary: "#F7F7F5",
    bgTertiary: "#F1F1EF",
    text: "#37352F",
    textSecondary: "#6B6B6B",
    textTertiary: "#9B9A97",
    border: "#E9E9E7",
    borderHover: "#D3D3D0",
    borderFocus: "#2383E2",
    accent: "#2383E2",
    accentLight: "#E8F0FE",
    success: "#0F7B6C",
    successBg: "#DBEDDB",
    warning: "#D9730D",
    warningBg: "#FADEC9",
    error: "#E03E3E",
    errorBg: "#FBE4E4",
    cardShadow: "0 1px 3px rgba(0,0,0,0.04)",
    hoverBg: "rgba(55, 53, 47, 0.04)",
  },
  dark: {
    bg: "#191919",
    bgSecondary: "#202020",
    bgTertiary: "#2B2B2B",
    text: "#E8E8E6",
    textSecondary: "#9B9A97",
    textTertiary: "#6B6B6B",
    border: "#333333",
    borderHover: "#444444",
    borderFocus: "#529CCA",
    accent: "#529CCA",
    accentLight: "#143A52",
    success: "#4DAB9A",
    successBg: "#1A2F2A",
    warning: "#C77D48",
    warningBg: "#2E2215",
    error: "#D44C47",
    errorBg: "#3D2020",
    cardShadow: "0 1px 3px rgba(0,0,0,0.2)",
    hoverBg: "rgba(255, 255, 255, 0.04)",
  },
};

// ============================================================
// SECTION HEADER
// ============================================================
const SectionHeader: React.FC<{
  icon: React.ReactElement;
  title: string;
  subtitle?: string;
  darkMode: boolean;
}> = ({ icon, title, subtitle, darkMode }) => {
  const p = darkMode ? PALETTE.dark : PALETTE.light;
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "8px",
          bgcolor: p.accentLight,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: p.accent,
          "& svg": { fontSize: 18 },
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: "0.95rem",
            fontWeight: 650,
            color: p.text,
            letterSpacing: "-0.01em",
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            sx={{
              fontSize: "0.78rem",
              color: p.textTertiary,
              lineHeight: 1.3,
              mt: 0.2,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

// ============================================================
// FIELD LABEL
// ============================================================
const FieldLabel: React.FC<{
  label: string;
  required?: boolean;
  darkMode: boolean;
}> = ({ label, required = false, darkMode }) => {
  const p = darkMode ? PALETTE.dark : PALETTE.light;
  return (
    <Typography
      sx={{
        fontSize: "0.78rem",
        fontWeight: 600,
        color: p.textSecondary,
        mb: 0.5,
        letterSpacing: "0.01em",
        textTransform: "uppercase",
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: p.error, ml: 0.3 }}>
          *
        </Box>
      )}
    </Typography>
  );
};

// ============================================================
// PROVEEDOR PREVIEW CARD
// ============================================================
const ProveedorPreview: React.FC<{
  proveedor: ProveedorItem | null;
  darkMode: boolean;
}> = ({ proveedor, darkMode }) => {
  const p = darkMode ? PALETTE.dark : PALETTE.light;
  if (!proveedor) return null;

  return (
    <Collapse in={!!proveedor}>
      <Box
        sx={{
          mt: 2,
          p: 2.5,
          borderRadius: "10px",
          border: `1px solid ${p.border}`,
          bgcolor: p.bgSecondary,
          transition: "all 0.2s ease",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <CheckCircle sx={{ fontSize: 16, color: p.success }} />
          <Typography
            sx={{
              fontSize: "0.78rem",
              fontWeight: 600,
              color: p.success,
              textTransform: "uppercase",
              letterSpacing: "0.03em",
            }}
          >
            Proveedor seleccionado
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "10px",
              bgcolor: p.accentLight,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.1rem",
              fontWeight: 700,
              color: p.accent,
              flexShrink: 0,
            }}
          >
            {proveedor.Nombre[0]}
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: "0.95rem",
                fontWeight: 650,
                color: p.text,
                lineHeight: 1.3,
              }}
            >
              {proveedor.Nombre}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.78rem",
                color: p.textTertiary,
                lineHeight: 1.3,
              }}
            >
              NIT/Doc: {proveedor.NumeroIdentificacion || "—"}
            </Typography>
          </Box>
        </Stack>

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {proveedor.Telefono && (
            <Grid item xs={12} sm={4}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Phone sx={{ fontSize: 14, color: p.textTertiary }} />
                <Typography sx={{ fontSize: "0.82rem", color: p.textSecondary }}>
                  {proveedor.Telefono}
                </Typography>
              </Stack>
            </Grid>
          )}
          {proveedor.Correo && (
            <Grid item xs={12} sm={4}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Email sx={{ fontSize: 14, color: p.textTertiary }} />
                <Typography
                  sx={{
                    fontSize: "0.82rem",
                    color: p.textSecondary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {proveedor.Correo}
                </Typography>
              </Stack>
            </Grid>
          )}
          {proveedor.Direccion && (
            <Grid item xs={12} sm={4}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <LocationOn sx={{ fontSize: 14, color: p.textTertiary }} />
                <Typography sx={{ fontSize: "0.82rem", color: p.textSecondary }}>
                  {proveedor.Direccion}
                </Typography>
              </Stack>
            </Grid>
          )}
        </Grid>
      </Box>
    </Collapse>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const CompraForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const proveedoresApi = useProveedoresApi();
  const [proveedores, setProveedores] = useState<ProveedorItem[]>([]);
  const [selectedProveedor, setSelectedProveedor] = useState<ProveedorItem | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? PALETTE.dark : PALETTE.light;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CompraFormInputs>({
    defaultValues: {
      FechaSolicitud: new Date(),
      FechaEntrega: null,
      IdEstadoCompra: 1,
      Activo: true,
      IdUsuario: user?.Id || 0,
    },
    mode: "onChange",
  });

  useEffect(() => {
    loadProveedores();
    if (id) loadCompra(parseInt(id));
  }, [id]);

  const loadProveedores = async () => {
    try {
      const data = await proveedoresApi.getAll();
      setProveedores(data);
    } catch (error) {
      console.error("Error cargando proveedores:", error);
    }
  };

  const loadCompra = async (compraId: number) => {
    try {
      const data = await comprasApi.getCompraById(compraId);
      reset({
        ...data,
        FechaSolicitud: /^\d{4}-\d{2}-\d{2}$/.test(data.FechaSolicitud) ? new Date(data.FechaSolicitud + 'T12:00:00') : new Date(data.FechaSolicitud),
        FechaEntrega: data.FechaEntrega ? (/^\d{4}-\d{2}-\d{2}$/.test(data.FechaEntrega) ? new Date(data.FechaEntrega + 'T12:00:00') : new Date(data.FechaEntrega)) : null,
      });
      if (data.IdProveedor) {
        const prov = proveedores.find((pr) => pr.Id === data.IdProveedor);
        if (prov) setSelectedProveedor(prov);
      }
    } catch (error) {
      console.error("Error al cargar la compra:", error);
    }
  };

  const handleFormSubmit = async (data: CompraFormInputs) => {
    try {
      const formattedData = {
        ...data,
        FechaSolicitud: data.FechaSolicitud.toISOString().split("T")[0],
        FechaEntrega: data.FechaEntrega
          ? data.FechaEntrega.toISOString().split("T")[0]
          : undefined,
        IdUsuario: user?.Id,
      };

      if (id) {
        await comprasApi.updateCompra(parseInt(id), formattedData);
      } else {
        await comprasApi.createCompra(formattedData);
      }

      setSaveSuccess(true);
      setTimeout(() => navigate("/compras"), 800);
    } catch (error) {
      console.error("Error al guardar la compra:", error);
    }
  };

  // ── Action buttons ──
  const formActions = (
    <Stack direction="row" spacing={1.5}>
      <Button
        variant="text"
        onClick={() => navigate("/compras")}
        startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
        sx={{
          color: p.textSecondary,
          px: 2,
          py: 1,
          "&:hover": { bgcolor: p.hoverBg },
        }}
      >
        Volver
      </Button>
      <Button
        form="compra-form"
        type="submit"
        variant="contained"
        disabled={isSubmitting || Object.keys(errors).length > 0}
        disableElevation
        startIcon={
          isSubmitting ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <Save sx={{ fontSize: 16 }} />
          )
        }
        sx={{
          bgcolor: p.accent,
          color: "#FFFFFF",
          px: 3,
          py: 1,
          "&:hover": {
            bgcolor: darkMode ? "#6BAFDB" : "#1B6FC2",
          },
          "&.Mui-disabled": {
            bgcolor: alpha(p.accent, 0.3),
            color: alpha("#FFFFFF", 0.5),
          },
        }}
      >
        {isSubmitting ? "Guardando..." : "Guardar Compra"}
      </Button>
    </Stack>
  );

  return (
    <ActionPageLayout
        icon={ShoppingCartIcon}
        title={id ? "Editar Compra" : "Nueva Compra"}
        actions={formActions}
      >
        <Fade in timeout={350}>
          <Box>
            {/* ── Success Alert ── */}
            <Collapse in={saveSuccess}>
              <Alert
                severity="success"
                icon={<CheckCircle sx={{ fontSize: 18 }} />}
                sx={{
                  mb: 3,
                  borderRadius: "10px",
                  border: `1px solid ${p.success}`,
                  bgcolor: p.successBg,
                  color: p.success,
                  "& .MuiAlert-icon": { color: p.success },
                }}
              >
                Compra guardada correctamente. Redirigiendo...
              </Alert>
            </Collapse>

            <form id="compra-form" onSubmit={handleSubmit(handleFormSubmit)}>
              {/* ── SECCIÓN 1: Datos Generales ── */}
              <Paper
                elevation={0}
                sx={{
                  p: 3.5,
                  mb: 3,
                  borderRadius: "12px",
                  border: `1px solid ${p.border}`,
                  bgcolor: p.bg,
                  boxShadow: p.cardShadow,
                }}
              >
                <SectionHeader
                  icon={<Info />}
                  title="Datos Generales"
                  subtitle="Información básica de la orden de compra"
                  darkMode={darkMode}
                />

                <Grid container spacing={3}>
                  {/* Orden de Compra */}
                  <Grid item xs={12} md={4}>
                    <FieldLabel label="Orden de Compra" darkMode={darkMode} />
                    <Controller
                      name="OrdenCompra"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          size="small"
                          fullWidth
                          disabled
                          placeholder="Se genera automáticamente"
                          InputProps={{
                            sx: {
                              bgcolor: p.bgTertiary,
                              fontFamily: "'IBM Plex Mono', monospace",
                              fontWeight: 600,
                              color: p.textSecondary,
                              "&.Mui-disabled": {
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: p.border,
                                },
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Estado */}
                  <Grid item xs={12} md={4}>
                    <FieldLabel label="Estado" darkMode={darkMode} />
                    <Box
                      sx={{
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        px: 1.5,
                        borderRadius: "8px",
                        border: `1px solid ${p.border}`,
                        bgcolor: p.bgTertiary,
                      }}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.75,
                          px: 1.5,
                          py: 0.4,
                          borderRadius: "6px",
                          bgcolor: p.warningBg,
                          color: p.warning,
                          fontSize: "0.8rem",
                          fontWeight: 600,
                        }}
                      >
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            bgcolor: p.warning,
                          }}
                        />
                        Abierta
                      </Box>
                    </Box>
                  </Grid>

                  {/* Creado por */}
                  <Grid item xs={12} md={4}>
                    <FieldLabel label="Creado por" darkMode={darkMode} />
                    <Box
                      sx={{
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        px: 1.5,
                        borderRadius: "8px",
                        border: `1px solid ${p.border}`,
                        bgcolor: p.bgTertiary,
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box
                          sx={{
                            width: 26,
                            height: 26,
                            borderRadius: "6px",
                            bgcolor: p.accentLight,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            color: p.accent,
                          }}
                        >
                          {(user?.Nombre || user?.Usuario || "U")[0]}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "0.84rem",
                            color: p.textSecondary,
                            fontWeight: 500,
                          }}
                        >
                          {user?.Nombre || user?.Usuario || "Usuario"}
                        </Typography>
                      </Stack>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* ── SECCIÓN 2: Fechas ── */}
              <Paper
                elevation={0}
                sx={{
                  p: 3.5,
                  mb: 3,
                  borderRadius: "12px",
                  border: `1px solid ${p.border}`,
                  bgcolor: p.bg,
                  boxShadow: p.cardShadow,
                }}
              >
                <SectionHeader
                  icon={<CalendarToday />}
                  title="Fechas"
                  subtitle="Fechas de solicitud y entrega estimada"
                  darkMode={darkMode}
                />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FieldLabel label="Fecha de Solicitud" darkMode={darkMode} />
                    <Controller
                      name="FechaSolicitud"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <DatePicker
                          value={value}
                          onChange={onChange}
                          readOnly
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              InputProps: {
                                sx: {
                                  bgcolor: p.bgTertiary,
                                  "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                                    borderColor: p.border,
                                  },
                                },
                              },
                              inputProps: { readOnly: true },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FieldLabel label="Fecha Estimada de Entrega" required darkMode={darkMode} />
                    <Controller
                      name="FechaEntrega"
                      control={control}
                      rules={{ required: "La fecha de entrega es requerida" }}
                      render={({ field: { value, onChange }, fieldState }) => (
                        <DatePicker
                          value={value}
                          onChange={onChange}
                          minDate={new Date()}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              error: !!fieldState.error,
                              helperText: fieldState.error?.message,
                              sx: {
                                "& .MuiFormHelperText-root": {
                                  fontSize: "0.75rem",
                                  mt: 0.5,
                                },
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* ── SECCIÓN 3: Proveedor ── */}
              <Paper
                elevation={0}
                sx={{
                  p: 3.5,
                  borderRadius: "12px",
                  border: `1px solid ${p.border}`,
                  bgcolor: p.bg,
                  boxShadow: p.cardShadow,
                }}
              >
                <SectionHeader
                  icon={<Business />}
                  title="Proveedor"
                  subtitle="Seleccione el proveedor para esta orden de compra"
                  darkMode={darkMode}
                />

                <Controller
                  name="IdProveedor"
                  control={control}
                  rules={{ required: "El proveedor es requerido" }}
                  render={({ field: { onChange, value }, fieldState }) => (
                    <Autocomplete
                      id="proveedor-autocomplete"
                      options={proveedores.filter((pr) => pr.Activo)}
                      getOptionLabel={(option: ProveedorItem) => option.Nombre}
                      value={proveedores.find((pr) => pr.Id === value) || null}
                      onChange={(_, newValue: ProveedorItem | null) => {
                        onChange(newValue ? newValue.Id : null);
                        setSelectedProveedor(newValue);
                      }}
                      loading={proveedoresApi.isLoading}
                      noOptionsText="Sin proveedores disponibles"
                      loadingText="Cargando proveedores..."
                      renderOption={(props, option) => (
                        <Box
                          component="li"
                          {...props}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            py: 1.2,
                            px: 2,
                            "&:hover": { bgcolor: p.hoverBg },
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "8px",
                              bgcolor: p.accentLight,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.82rem",
                              fontWeight: 700,
                              color: p.accent,
                              flexShrink: 0,
                            }}
                          >
                            {option.Nombre[0]}
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.86rem",
                                fontWeight: 600,
                                color: p.text,
                              }}
                            >
                              {option.Nombre}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color: p.textTertiary,
                              }}
                            >
                              {option.Telefono || ""} · {option.Correo || ""}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Buscar proveedor por nombre..."
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <Business
                                  sx={{ fontSize: 18, color: p.textTertiary, ml: 0.5 }}
                                />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <>
                                {proveedoresApi.isLoading ? (
                                  <CircularProgress size={18} color="inherit" />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          sx={{
                            "& .MuiFormHelperText-root": {
                              fontSize: "0.75rem",
                              mt: 0.5,
                            },
                          }}
                        />
                      )}
                    />
                  )}
                />

                {/* Preview del proveedor seleccionado */}
                <ProveedorPreview proveedor={selectedProveedor} darkMode={darkMode} />
              </Paper>

              {/* ── Errores ── */}
              <Collapse in={Object.keys(errors).length > 0}>
                <Alert
                  severity="error"
                  icon={false}
                  sx={{
                    mt: 3,
                    borderRadius: "10px",
                    border: `1px solid ${p.error}`,
                    bgcolor: p.errorBg,
                    color: p.error,
                    fontSize: "0.84rem",
                    fontWeight: 500,
                  }}
                >
                  Complete todos los campos requeridos antes de guardar.
                </Alert>
              </Collapse>
            </form>
          </Box>
        </Fade>
      </ActionPageLayout>
  );
};

export default CompraForm;
